import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import { UsersIcon } from '@heroicons/react/24/outline';
import { Badge } from '@tailwindComponents/Badge';
import { Button } from '@tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { SkuMenuOption } from '@views/SKUMaster/components/SkuActionsMenu';
import { Customer } from './customerTypes';
import { CustomerAccountGroup } from './customerGroups';
import { CustomerTableRow } from './CustomerTableRow';

export type CustomerSortColumn =
  | 'name'
  | 'customerId'
  | 'type'
  | 'market'
  | 'readiness'
  | 'status';

export interface CustomerTableProps {
  groups: CustomerAccountGroup[];
  isLoading: boolean;
  buildMenuOptions: (customer: Customer) => SkuMenuOption[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onAddCustomer: () => void;
  orderBy: CustomerSortColumn | null;
  isOrderAscending: boolean;
  onSortColumn: (column: CustomerSortColumn) => void;
}

const headerCell = 'px-4 py-3 text-xs font-medium text-gray-500';

const groupAvatarColours = [
  'bg-slate-700',
  'bg-amber-700',
  'bg-indigo-600',
  'bg-emerald-700',
  'bg-rose-600',
  'bg-cyan-700',
];

const groupInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase() || '?';

const colourForGroup = (name: string): string => {
  const sum = [...name].reduce((total, char) => total + char.charCodeAt(0), 0);
  return groupAvatarColours[sum % groupAvatarColours.length];
};

function GroupAvatar({ name, code }: { name: string; code: string | null }) {
  return (
    <div
      aria-hidden='true'
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-white ${colourForGroup(
        name
      )}`}
    >
      {code ?? groupInitials(name)}
    </div>
  );
}

export function CustomerTable({
  groups,
  isLoading,
  buildMenuOptions,
  hasActiveFilters,
  onClearFilters,
  onAddCustomer,
  orderBy,
  isOrderAscending,
  onSortColumn,
}: CustomerTableProps) {
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetTimeout.current), []);

  const visibleKeys = useMemo(() => groups.map((group) => group.key), [groups]);
  useEffect(() => {
    const present = new Set(visibleKeys);
    setCollapsedKeys((previous) => {
      const next = new Set([...previous].filter((key) => present.has(key)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleKeys]);

  const toggleCollapsed = (key: string) =>
    setCollapsedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const handleCopy = (customer: Customer) => {
    navigator.clipboard?.writeText(customer.customerId);
    setCopiedKey(customer.id);
    clearTimeout(copyResetTimeout.current);
    copyResetTimeout.current = setTimeout(() => setCopiedKey(null), 1500);
  };

  const sortArrow = (column: CustomerSortColumn) => {
    if (orderBy === column) {
      return isOrderAscending ? (
        <ChevronUpIcon className='inline h-4 w-4' />
      ) : (
        <ChevronDownIcon className='inline h-4 w-4' />
      );
    }
    return <ChevronUpDownIcon className='inline h-4 w-4 text-gray-300' />;
  };

  const sortableHeader = (label: string, column: CustomerSortColumn) => (
    <th
      scope='col'
      onClick={() => onSortColumn(column)}
      className={`${headerCell} cursor-pointer text-left hover:text-gray-700`}
    >
      {label} {sortArrow(column)}
    </th>
  );

  return (
    <div className='overflow-x-auto rounded-lg border border-gray-200 bg-white'>
      <table className='w-full min-w-[980px] table-auto'>
        <thead className='bg-gray-50'>
          <tr>
            <th scope='col' className='w-10 px-2'>
              <span className='sr-only'>Expand</span>
            </th>
            {sortableHeader('Name', 'name')}
            {sortableHeader('Customer ID', 'customerId')}
            {sortableHeader('Type', 'type')}
            {sortableHeader('Market', 'market')}
            <th scope='col' className={`${headerCell} text-left`}>
              Partners
            </th>
            {sortableHeader('Readiness', 'readiness')}
            {sortableHeader('Status', 'status')}
            <th scope='col' className={`${headerCell} text-right`}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200'>
          {isLoading && (
            <tr>
              <td colSpan={9} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <FloxSpinner size='md' />
                  <span className='text-sm text-gray-500'>
                    Loading customers...
                  </span>
                </div>
              </td>
            </tr>
          )}
          {!isLoading && groups.length === 0 && (
            <tr>
              <td colSpan={9} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <UsersIcon className='h-12 w-12 text-gray-300' />
                  <p className='text-sm font-medium text-gray-900'>
                    No customers found
                  </p>
                  {hasActiveFilters ? (
                    <>
                      <p className='text-sm text-gray-500'>
                        No customers match the current filters.
                      </p>
                      <Button
                        intent='tertiary'
                        size='sm'
                        label='Clear filters'
                        onClick={onClearFilters}
                      />
                    </>
                  ) : (
                    <>
                      <p className='text-sm text-gray-500'>
                        Add your first customer to get started.
                      </p>
                      <Button
                        intent='primary'
                        size='sm'
                        label='Add customer'
                        onClick={onAddCustomer}
                      />
                    </>
                  )}
                </div>
              </td>
            </tr>
          )}
          {!isLoading &&
            groups.map((group) => {
              const isCollapsed = collapsedKeys.has(group.key);
              return (
                <Fragment key={group.key}>
                  <tr
                    className='cursor-pointer bg-gray-50/70 hover:bg-gray-100'
                    onClick={() => toggleCollapsed(group.key)}
                  >
                    <td className='w-10 px-2 py-3'>
                      <button
                        type='button'
                        aria-expanded={!isCollapsed}
                        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${
                          group.name
                        }`}
                        className='rounded p-1 transition-colors hover:bg-gray-200'
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapsed(group.key);
                        }}
                      >
                        <ChevronRightIcon
                          className={`h-4 w-4 text-gray-500 transition-transform ${
                            isCollapsed ? '' : 'rotate-90'
                          }`}
                        />
                      </button>
                    </td>
                    <td colSpan={8} className='px-4 py-3'>
                      <div className='flex items-center gap-3'>
                        <GroupAvatar
                          name={group.name}
                          code={group.avatarCode}
                        />
                        <span className='text-sm font-semibold text-gray-900'>
                          {group.name}
                        </span>
                        <span className='text-xs text-gray-500'>
                          {group.customers.length} customer
                          {group.customers.length === 1 ? '' : 's'}
                          {group.isGroupingNode &&
                            ` · ${group.marketCount} market${
                              group.marketCount === 1 ? '' : 's'
                            }`}
                        </span>
                        {group.isGroupingNode && (
                          <Badge intent='neutral' label='Grouping node' />
                        )}
                      </div>
                    </td>
                  </tr>
                  {!isCollapsed &&
                    group.customers.map((customer) => (
                      <CustomerTableRow
                        key={customer.id}
                        customer={customer}
                        menuOptions={buildMenuOptions(customer)}
                        isCopied={copiedKey === customer.id}
                        onCopy={handleCopy}
                      />
                    ))}
                </Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
